import {
  getApps,
  initializeApp,
  type FirebaseApp,
  type FirebaseOptions
} from 'firebase/app'
import type {AppCheck} from 'firebase/app-check'

export const APP_CHECK_HEADER = 'X-Firebase-AppCheck'

const APP_CHECK_RETRY_DELAY_MS = 300

// Longest a single caller waits for a token before continuing without one. The
// request then fails fast against the API's App Check rejection instead of
// blocking until the client's 15s request timeout — which it would not even
// reach, since headers are resolved before the fetch is issued. Deliberately
// shorter than a slow script load: the caller's own retry, not this budget, is
// what waits out a slow provider.
const APP_CHECK_TOKEN_BUDGET_MS = 6000

// After a failed attempt, immediately following calls — the API client's own
// 401 retry and whatever else is queued — skip straight to "no token" instead
// of each waiting out the full budget again.
const APP_CHECK_UNAVAILABLE_COOLDOWN_MS = 750

// Google's documented alternative host for the same reCAPTCHA Enterprise
// service, for networks where www.google.com is not reachable
// (https://docs.cloud.google.com/recaptcha/docs/faq, "Can I use reCAPTCHA
// globally"). `www.google.com/recaptcha/enterprise.js` closes the connection
// from the affected network while this host answers normally; the site key,
// provider and token exchange are unchanged. Its bootstrap also points
// `__recaptcha_api` at the same host, so the widget requests follow it.
const RECAPTCHA_ENTERPRISE_SCRIPT_URL =
  'https://www.recaptcha.net/recaptcha/enterprise.js?render=explicit'
const RECAPTCHA_ENTERPRISE_SCRIPT_ID = 'dade-recaptcha-enterprise-script'
const RECAPTCHA_ENTERPRISE_POLL_INTERVAL_MS = 50

// Generous: the bootstrap plus its widget code has been observed taking over
// fourteen seconds on a slow load. This bounds readiness, not the caller —
// callers stop waiting after APP_CHECK_TOKEN_BUDGET_MS and a later request
// picks up the same attempt once it finishes.
const RECAPTCHA_ENTERPRISE_READY_TIMEOUT_MS = 25000

// A throttled provider rejects immediately for a fixed window, so repeating that
// attempt only delays the caller. A provider that could not be loaded at all is
// retried by the caller's own schedule rather than by re-requesting the script
// a few hundred milliseconds later. Every other failure — most commonly
// `appCheck/recaptcha-error` while the reCAPTCHA widget is still initializing —
// can succeed on a second attempt.
const NON_RETRYABLE_APP_CHECK_ERROR_CODES = [
  'appcheck/throttled',
  'appcheck/initial-throttle'
]

type BrowserWithDadeAppCheck = Window & {
  __DADE_GET_APP_CHECK_TOKEN__?: () => Promise<string | null>
}

type RecaptchaEnterpriseApi = {
  ready?: (callback: () => void) => void
  render?: unknown
  execute?: unknown
}

type BrowserWithRecaptcha = Window & {
  grecaptcha?: {enterprise?: RecaptchaEnterpriseApi}
}

/**
 * The reCAPTCHA Enterprise provider could not be made ready. Distinct from an
 * SDK error so an immediate in-process retry can be skipped for it.
 */
class AppCheckProviderUnavailableError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AppCheckProviderUnavailableError'
  }
}

let appCheckInstance: AppCheck | null = null
let appCheckInitializationPromise: Promise<AppCheck> | null = null
let recaptchaEnterpriseReadyPromise: Promise<void> | null = null
let recaptchaEnterpriseIsReady = false
let appCheckUnavailableUntil = 0

function getCurrentEnvironment(): string {
  return `${
    process.env.REACT_APP_PUBLIC_APPLICATION_ENVIRONMENT || 'development'
  }`
    .trim()
    .toLowerCase()
}

function appCheckIsEnabledForCurrentBrowser(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  const isLocalBrowser =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'

  if (!isLocalBrowser) {
    return true
  }

  return (
    `${process.env.REACT_APP_LOCAL_APP_CHECK_ENABLED || ''}`
      .trim()
      .toLowerCase() === 'true'
  )
}

function resolveAppCheckSiteKey(): string {
  const environment = getCurrentEnvironment()
  const byEnvironment: Record<string, string | undefined> = {
    development: process.env.REACT_APP_PUBLIC_DEVELOPMENT_APP_CHECK_SITE_KEY,
    staging: process.env.REACT_APP_PUBLIC_STAGING_APP_CHECK_SITE_KEY,
    production: process.env.REACT_APP_PUBLIC_PRODUCTION_APP_CHECK_SITE_KEY
  }

  return `${
    byEnvironment[environment] ||
    process.env.REACT_APP_PUBLIC_APP_CHECK_SITE_KEY ||
    ''
  }`.trim()
}

function resolveFirebaseConfig(): FirebaseOptions | null {
  const environment = getCurrentEnvironment()
  const byEnvironment: Record<string, FirebaseOptions> = {
    development: {
      appId: process.env.REACT_APP_PUBLIC_DEVELOPMENT_APP_ID,
      apiKey: process.env.REACT_APP_PUBLIC_DEVELOPMENT_API_KEY,
      authDomain: process.env.REACT_APP_PUBLIC_DEVELOPMENT_AUTH_DOMAIN,
      databaseURL: process.env.REACT_APP_PUBLIC_DEVELOPMENT_DATABASE_URL,
      projectId: process.env.REACT_APP_PUBLIC_DEVELOPMENT_PROJECT_ID,
      storageBucket: process.env.REACT_APP_PUBLIC_DEVELOPMENT_STORAGE_BUCKET
    },
    staging: {
      appId: process.env.REACT_APP_PUBLIC_STAGING_APP_ID,
      apiKey: process.env.REACT_APP_PUBLIC_STAGING_API_KEY,
      authDomain: process.env.REACT_APP_PUBLIC_STAGING_AUTH_DOMAIN,
      databaseURL: process.env.REACT_APP_PUBLIC_STAGING_DATABASE_URL,
      projectId: process.env.REACT_APP_PUBLIC_STAGING_PROJECT_ID,
      storageBucket: process.env.REACT_APP_PUBLIC_STAGING_STORAGE_BUCKET
    },
    production: {
      appId: process.env.REACT_APP_PUBLIC_PRODUCTION_APP_ID,
      apiKey: process.env.REACT_APP_PUBLIC_PRODUCTION_API_KEY,
      authDomain: process.env.REACT_APP_PUBLIC_PRODUCTION_AUTH_DOMAIN,
      databaseURL: process.env.REACT_APP_PUBLIC_PRODUCTION_DATABASE_URL,
      projectId: process.env.REACT_APP_PUBLIC_PRODUCTION_PROJECT_ID,
      storageBucket: process.env.REACT_APP_PUBLIC_PRODUCTION_STORAGE_BUCKET
    }
  }
  const config = byEnvironment[environment]

  return config?.appId && config?.apiKey && config?.projectId ? config : null
}

function getOrInitializeFirebaseApp(): FirebaseApp | null {
  const existingApp = getApps()[0]
  if (existingApp) {
    return existingApp
  }

  const config = resolveFirebaseConfig()
  return config ? initializeApp(config) : null
}

function getRecaptchaEnterprise(): RecaptchaEnterpriseApi | undefined {
  if (typeof window === 'undefined') return undefined

  return (window as BrowserWithRecaptcha).grecaptcha?.enterprise
}

// The bootstrap creates `grecaptcha.enterprise` with nothing but a `ready` that
// queues callbacks, then requests the widget code that actually fills it in. So
// the object existing proves nothing; only the widget code brings `render` and
// `execute`, which is what the SDK calls.
function recaptchaEnterpriseWidgetCodeIsLoaded(): boolean {
  const enterprise = getRecaptchaEnterprise()

  return (
    typeof enterprise?.render === 'function' &&
    typeof enterprise?.execute === 'function'
  )
}

/**
 * Bring the reCAPTCHA Enterprise provider to a genuinely ready state ourselves,
 * with the error and timeout handling the SDK's loader does not have.
 *
 * `@firebase/app-check` sets only `script.onload` on its injected script. When
 * that request fails — the observed case is `net::ERR_CONNECTION_CLOSED` — its
 * readiness deferred never settles, the token exchange promise it feeds is
 * never cleared, and every later `getToken` call awaits that dead promise for
 * the lifetime of the page. The same poisoning happens one step later if the
 * bootstrap runs but its widget code does not arrive: the SDK would call the
 * queueing `ready` stub and wait forever.
 *
 * Resolving only once a `ready` callback has actually fired means
 * `initializeAppCheck` is reached only when the SDK's own path can complete.
 */
function requestRecaptchaEnterpriseReadiness(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    let settled = false
    let readyRequested = false
    let pollTimer: ReturnType<typeof setInterval> | undefined
    let timeoutTimer: ReturnType<typeof setTimeout> | undefined
    let script: HTMLScriptElement | undefined

    const cleanup = () => {
      if (pollTimer !== undefined) clearInterval(pollTimer)
      if (timeoutTimer !== undefined) clearTimeout(timeoutTimer)
      script?.removeEventListener('error', fail)
    }

    // Also the landing point for a late callback from an attempt that already
    // timed out, which must not revive it.
    function succeed() {
      if (settled) return
      settled = true
      cleanup()
      recaptchaEnterpriseIsReady = true
      resolve()
    }

    function fail() {
      if (settled) return
      settled = true
      cleanup()
      // Drop our own dead tag so the next attempt issues a new request instead
      // of waiting on a script element that will never load. The provider's own
      // globals are left alone.
      script?.remove()
      reject(
        new AppCheckProviderUnavailableError(
          'The reCAPTCHA Enterprise provider did not become ready.'
        )
      )
    }

    const requestReady = () => {
      if (readyRequested) return
      const enterprise = getRecaptchaEnterprise()
      if (typeof enterprise?.ready !== 'function') return

      readyRequested = true
      enterprise.ready(succeed)
    }

    // A provider left ready by an earlier attempt answers this immediately.
    requestReady()
    if (settled) return

    if (!recaptchaEnterpriseWidgetCodeIsLoaded()) {
      // Either nothing is loaded yet, or only a stub whose widget code never
      // arrived. Re-running the bootstrap re-requests that code and flushes the
      // queued callbacks — including the one just registered — when it lands.
      document.getElementById(RECAPTCHA_ENTERPRISE_SCRIPT_ID)?.remove()

      script = document.createElement('script')
      script.id = RECAPTCHA_ENTERPRISE_SCRIPT_ID
      script.src = RECAPTCHA_ENTERPRISE_SCRIPT_URL
      script.async = true
      script.addEventListener('error', fail, {once: true})
      document.head.appendChild(script)
    }

    // Poll until the bootstrap has installed `ready`; from then on the callback
    // it fires, not the poll, is what resolves this.
    pollTimer = setInterval(requestReady, RECAPTCHA_ENTERPRISE_POLL_INTERVAL_MS)
    timeoutTimer = setTimeout(fail, RECAPTCHA_ENTERPRISE_READY_TIMEOUT_MS)
  })
}

function ensureRecaptchaEnterpriseReady(): Promise<void> {
  if (recaptchaEnterpriseIsReady && getRecaptchaEnterprise()) {
    return Promise.resolve()
  }

  if (typeof document === 'undefined') {
    return Promise.reject(
      new AppCheckProviderUnavailableError(
        'No document to load reCAPTCHA into.'
      )
    )
  }

  if (!recaptchaEnterpriseReadyPromise) {
    recaptchaEnterpriseReadyPromise =
      requestRecaptchaEnterpriseReadiness().catch((error) => {
        // Only our own failed attempt is discarded, so a later one starts over
        // instead of short-cutting through a leftover stub.
        recaptchaEnterpriseReadyPromise = null
        throw error
      })
  }

  return recaptchaEnterpriseReadyPromise
}

async function getOrInitializeAppCheck(
  app: FirebaseApp,
  siteKey: string
): Promise<AppCheck> {
  if (appCheckInstance) {
    return appCheckInstance
  }

  if (!appCheckInitializationPromise) {
    appCheckInitializationPromise = (async () => {
      const {ReCaptchaEnterpriseProvider, initializeAppCheck} = await import(
        'firebase/app-check'
      )

      // Several API requests commonly begin together during initial render. Keep
      // App Check initialization single-flight so a second request cannot treat
      // Firebase's "already initialized" response as a permanent failure.
      if (!appCheckInstance) {
        appCheckInstance = initializeAppCheck(app, {
          provider: new ReCaptchaEnterpriseProvider(siteKey),
          isTokenAutoRefreshEnabled: true
        })
      }

      return appCheckInstance
    })()
  }

  try {
    return await appCheckInitializationPromise
  } finally {
    appCheckInitializationPromise = null
  }
}

function isRetryableAppCheckError(error: unknown): boolean {
  if (error instanceof AppCheckProviderUnavailableError) {
    return false
  }

  const code = `${(error as {code?: string} | null)?.code || ''}`
    .trim()
    .toLowerCase()

  return !NON_RETRYABLE_APP_CHECK_ERROR_CODES.includes(code)
}

function waitBeforeRetry(delayMs: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, delayMs)
  })
}

async function acquireAppCheckToken(
  app: FirebaseApp,
  siteKey: string,
  forceRefresh: boolean
): Promise<string | null> {
  const requestToken = async (refresh: boolean) => {
    await ensureRecaptchaEnterpriseReady()
    const [{getToken}, initializedAppCheck] = await Promise.all([
      import('firebase/app-check'),
      getOrInitializeAppCheck(app, siteKey)
    ])
    const result = await getToken(initializedAppCheck, refresh)
    return result.token || null
  }

  try {
    return await requestToken(forceRefresh)
  } catch (error) {
    // App Check can fail transiently while the reCAPTCHA widget is still coming
    // up, and the first API requests of a page load are the ones most likely to
    // hit that window. Give the provider a second chance instead of spending the
    // request — and every retry the caller makes — on an unverified attempt.
    if (!isRetryableAppCheckError(error)) {
      return null
    }
  }

  try {
    await waitBeforeRetry(APP_CHECK_RETRY_DELAY_MS)
    return await requestToken(true)
  } catch {
    return null
  }
}

export async function getAppCheckToken(
  forceRefresh = false
): Promise<string | null> {
  if (!appCheckIsEnabledForCurrentBrowser()) {
    return null
  }

  const siteKey = resolveAppCheckSiteKey()
  const app = getOrInitializeFirebaseApp()
  if (!siteKey || !app) {
    return null
  }

  if (Date.now() < appCheckUnavailableUntil) {
    return null
  }

  let budgetTimer: ReturnType<typeof setTimeout> | undefined
  const budget = new Promise<null>((resolve) => {
    budgetTimer = setTimeout(() => resolve(null), APP_CHECK_TOKEN_BUDGET_MS)
  })

  let token: string | null = null
  try {
    // Whichever comes first: a token, or the caller's budget. A slow load is not
    // abandoned — it keeps running behind this call, so the next request can use
    // it — but no single request waits on it indefinitely.
    token = await Promise.race([
      acquireAppCheckToken(app, siteKey, forceRefresh),
      budget
    ])
  } finally {
    if (budgetTimer !== undefined) clearTimeout(budgetTimer)
  }

  // Do not disable App Check for the remainder of the session: without a token
  // the request proceeds unverified and the API rejects it, and later requests
  // can recover without a page reload.
  appCheckUnavailableUntil = token
    ? 0
    : Date.now() + APP_CHECK_UNAVAILABLE_COOLDOWN_MS

  return token
}

export async function getAppCheckHeaders(
  forceRefresh = false
): Promise<Record<string, string>> {
  const token = await getAppCheckToken(forceRefresh)
  return token ? {[APP_CHECK_HEADER]: token} : {}
}

export function registerAppCheckTokenGetter() {
  if (typeof window === 'undefined') {
    return
  }

  ;(window as BrowserWithDadeAppCheck).__DADE_GET_APP_CHECK_TOKEN__ =
    getAppCheckToken
}
